import React, { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, Save, Info, Image as ImageIcon, Upload, Trash2, Gavel } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import DocumentiOrfaniPanel from "../components/DocumentiOrfaniPanel";
import { ModalitaPicker, MODALITA_LABEL } from "../components/DisciplinaGuida";
import { ImpostazioniSanitarieCard } from "../components/ImpostazioniSanitarie";
import { SollecitiSettingsCard } from "../components/Solleciti";
import { ImpTessere, ImpLinks } from "../components/ImpostazioniHub";
import { ConsegneAutoCard } from "../components/ConsegneAutoImpostazioni";


export default function Impostazioni() {
  const { reload } = useAuth();
  const [cat, setCatS] = useState(() => new URLSearchParams(window.location.search).get("cat") || null);
  const setCat = (c) => { setCatS(c); window.history.replaceState(null, "", c ? `?cat=${c}` : window.location.pathname); };
  const qc = useQueryClient();
  const [fromName, setFromName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [heading, setHeading] = useState("");
  const [fIndirizzo, setFIndirizzo] = useState("");
  const [fTelefono, setFTelefono] = useState("");
  const [fSito, setFSito] = useState("");
  const [richiamiAuto, setRichiamiAuto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logo, setLogo] = useState("");
  const [logoBusy, setLogoBusy] = useState(false);
  const fileRef = useRef(null);

  const [discSoglia, setDiscSoglia] = useState(5);
  const [discLettere, setDiscLettere] = useState(3);
  const [discFinestra, setDiscFinestra] = useState(12);
  const [discSollecito, setDiscSollecito] = useState(3);
  const [discModalita, setDiscModalita] = useState("progressiva");
  const [discPeriodo, setDiscPeriodo] = useState(12);
  const [discScadenza, setDiscScadenza] = useState(12);
  const [discSaving, setDiscSaving] = useState(false);

  const { data } = useQuery({
    queryKey: ["impostazioni-email"],
    queryFn: async () => (await api.get("/impostazioni/email")).data,
  });
  const { data: logoData } = useQuery({
    queryKey: ["impostazioni-logo"],
    queryFn: async () => (await api.get("/impostazioni/logo")).data,
  });
  const { data: discData } = useQuery({
    queryKey: ["impostazioni-disciplina"],
    queryFn: async () => (await api.get("/impostazioni/disciplina")).data,
  });
  useEffect(() => {
    if (discData) {
      setDiscSoglia(discData.disc_soglia_mese ?? 5);
      setDiscLettere(discData.disc_lettere_allerta ?? 3);
      setDiscFinestra(discData.disc_finestra_mesi ?? 12);
      setDiscSollecito(discData.disc_sollecito_giorni ?? 3);
      setDiscModalita(discData.disc_modalita || "progressiva");
      setDiscPeriodo(discData.disc_periodo_mesi ?? 12);
      setDiscScadenza(discData.disc_scadenza_mesi ?? 12);
    }
  }, [discData]);
  useEffect(() => {
    if (data) {
      setFromName(data.email_from_name || ""); setReplyTo(data.email_reply_to || "");
      setHeading(data.email_heading || ""); setFIndirizzo(data.footer_indirizzo || "");
      setFTelefono(data.footer_telefono || ""); setFSito(data.footer_sito || "");
      setRichiamiAuto(!!data.richiami_auto_email);
    }
  }, [data]);
  useEffect(() => { if (logoData) setLogo(logoData.logo || ""); }, [logoData]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/impostazioni/email", {
        email_from_name: fromName, email_reply_to: replyTo, email_heading: heading,
        footer_indirizzo: fIndirizzo, footer_telefono: fTelefono, footer_sito: fSito,
        richiami_auto_email: richiamiAuto,
      });
      toast.success("Impostazioni email salvate");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const saveDisc = async () => {
    setDiscSaving(true);
    try {
      const inUso = discData?.disc_modalita || "progressiva";
      if (discModalita !== inUso && !window.confirm(`Stai passando dalla modalità "${MODALITA_LABEL[inUso]}" a "${MODALITA_LABEL[discModalita]}".\n\n• Le lettere di richiamo già emesse restano come sono.\n• Da adesso il sistema conta con la nuova regola, usando solo le segnalazioni non ancora finite in una lettera.\n\nVuoi continuare?`)) { setDiscSaving(false); return; }
      await api.put("/impostazioni/disciplina", {
        disc_modalita: discModalita, disc_periodo_mesi: Number(discPeriodo), disc_scadenza_mesi: Number(discScadenza),
        disc_soglia_mese: Number(discSoglia), disc_lettere_allerta: Number(discLettere), disc_finestra_mesi: Number(discFinestra), disc_sollecito_giorni: Number(discSollecito),
      });
      toast.success("Impostazioni disciplina salvate");
      qc.invalidateQueries({ queryKey: ["impostazioni-disciplina"] });
      qc.invalidateQueries({ queryKey: ["disciplina-panoramica"] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio");
    } finally { setDiscSaving(false); }
  };

  const onPickLogo = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seleziona un file immagine"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = async () => {
        const max = 400;
        let { width: w, height: h } = img;
        if (w > max || h > max) { const r = Math.min(max / w, max / h); w = Math.round(w * r); h = Math.round(h * r); }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/png");
        await saveLogo(dataUrl);
      };
      img.onerror = () => toast.error("Immagine non valida");
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const saveLogo = async (dataUrl) => {
    setLogoBusy(true);
    try {
      await api.put("/impostazioni/logo", { logo: dataUrl });
      setLogo(dataUrl);
      await reload();
      toast.success(dataUrl ? "Logo salvato" : "Logo rimosso");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel salvataggio del logo");
    } finally { setLogoBusy(false); }
  };

  const pannello = (
    <>
      <ImpLinks cat={cat} />
      {cat === "sanitario" && <ImpostazioniSanitarieCard />}
      {cat === "consegne" && <ConsegneAutoCard />}

      {cat === "azienda" && (
      <div className="gr-card p-6 space-y-5" data-testid="logo-settings-card">
        <div className="flex items-center gap-2">
          <ImageIcon size={20} className="text-primary" />
          <h2 className="font-head font-bold text-lg">Logo aziendale</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Carica il logo della tua azienda: comparirà nella barra laterale, nella schermata di accesso con PIN degli
          operatori e nei documenti PDF (credenziali, guida). Se non carichi nulla, resta tutto come ora.
        </p>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="h-20 w-20 rounded-xl border grid place-items-center bg-secondary/40 overflow-hidden shrink-0" data-testid="logo-preview">
            {logo ? <img src={logo} alt="Logo" className="h-full w-full object-contain" /> : <ImageIcon size={26} className="text-muted-foreground" />}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickLogo} data-testid="logo-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={logoBusy} className="font-semibold" data-testid="logo-upload-btn">
              <Upload size={16} className="mr-1" /> {logo ? "Cambia logo" : "Carica logo"}
            </Button>
            {logo && (
              <Button variant="outline" onClick={() => saveLogo("")} disabled={logoBusy} className="text-destructive" data-testid="logo-remove-btn">
                <Trash2 size={16} className="mr-1" /> Rimuovi
              </Button>
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Formati consigliati: PNG o JPG. L'immagine viene ridimensionata automaticamente.</p>
      </div>
      )}

      {cat === "email" && (<>
      <div className="gr-card p-6 space-y-4" data-testid="richiami-email-card">
        <div className="flex items-center gap-2">
          <Mail size={20} className="text-primary" />
          <h2 className="font-head font-bold text-lg">Report via email</h2>
        </div>
        <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
          <div className="min-w-0">
            <div className="font-semibold text-sm">Invia automaticamente i report via email</div>
            <p className="text-xs text-muted-foreground mt-1">
              Ogni giorno alle <b>23:50</b> ogni operatore (che ha un'email registrata) riceve un riepilogo dei propri
              report della giornata — automatici (ritardi, ingressi mancanti…) e manuali (task non effettuate) —
              con un pulsante per <b>confermare la presa visione</b>. Chi non ha email non riceve nulla.
            </p>
          </div>
          <Switch checked={richiamiAuto} onCheckedChange={setRichiamiAuto} data-testid="richiami-auto-toggle" />
        </div>
        <Button onClick={save} disabled={saving} className="font-semibold" data-testid="richiami-auto-save">
          <Save size={16} className="mr-1" /> {saving ? "Salvataggio..." : "Salva"}
        </Button>
      </div>

      <div className="gr-card p-6 space-y-5" data-testid="email-settings-card">
        <div className="flex items-center gap-2">
          <Mail size={20} className="text-primary" />
          <h2 className="font-head font-bold text-lg">Email automatiche</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Impostazioni per le email inviate automaticamente (a operatori, ospiti/familiari e medico):
          buste paga, convenzioni, riepiloghi terapie, ecc.
        </p>

        <div className="space-y-2">
          <Label htmlFor="from-name">Nome mittente</Label>
          <Input id="from-name" data-testid="email-from-name" value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            placeholder={data?.default_from_name || "Es. Casa di Riposo La Villa"} />
          <p className="text-xs text-muted-foreground">È il nome che il destinatario vede come mittente dell'email.</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="reply-to">Email di risposta (Reply-To)</Label>
          <Input id="reply-to" data-testid="email-reply-to" type="email" value={replyTo}
            onChange={(e) => setReplyTo(e.target.value)}
            placeholder="es. amministrazione@tuodominio.it" />
          <p className="text-xs text-muted-foreground">Quando un destinatario risponde, la risposta arriva a questo indirizzo.</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email-heading">Intestazione comunicazioni (facoltativa)</Label>
          <Input id="email-heading" data-testid="email-heading-setting" value={heading}
            onChange={(e) => setHeading(e.target.value)}
            placeholder="Es. Casa di Riposo Orto della Longevità" />
          <p className="text-xs text-muted-foreground">Testo mostrato in alto nelle comunicazioni (email e PDF), sopra l'oggetto. Se vuoto, viene usato solo il logo.</p>
        </div>

        <div className="space-y-2 pt-2 border-t">
          <Label>Piè di pagina (mostrato in fondo a email e PDF)</Label>
          <Input data-testid="footer-indirizzo" value={fIndirizzo} onChange={(e) => setFIndirizzo(e.target.value)} placeholder="Indirizzo (es. Via Roma 1, 00100 Roma)" />
          <div className="grid sm:grid-cols-2 gap-2">
            <Input data-testid="footer-telefono" value={fTelefono} onChange={(e) => setFTelefono(e.target.value)} placeholder="Telefono (es. 06 1234567)" />
            <Input data-testid="footer-sito" value={fSito} onChange={(e) => setFSito(e.target.value)} placeholder="Sito web (es. www.tuosito.it)" />
          </div>
          <p className="text-xs text-muted-foreground">Compaiono centrati in fondo alle comunicazioni. Lascia vuoto ciò che non vuoi mostrare.</p>
        </div>

        <div className="rounded-lg bg-secondary/60 p-3 flex gap-2 text-xs text-muted-foreground" data-testid="email-info-note">
          <Info size={16} className="shrink-0 mt-0.5 text-primary" />
          <span>
            L'indirizzo tecnico del mittente è gestito dalla piattaforma (dominio condiviso e verificato) e
            non è modificabile per motivi anti-phishing. Puoi però personalizzare il <strong>nome mittente</strong> e
            l'<strong>email di risposta</strong> sul tuo dominio.
          </span>
        </div>

        <Button onClick={save} disabled={saving} className="font-semibold" data-testid="email-settings-save">
          <Save size={16} className="mr-1" /> {saving ? "Salvataggio..." : "Salva impostazioni"}
        </Button>
      </div>
      </>)}

      {cat === "operatori" && <SollecitiSettingsCard />}

      {cat === "disciplina" && (
      <div className="gr-card p-6 space-y-5" data-testid="disciplina-settings-card">
        <div className="flex items-center gap-2">
          <Gavel size={20} className="text-red-600" />
          <h2 className="font-head font-bold text-lg">Disciplina · Segnalazioni operatori</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Regole per le segnalazioni disciplinari degli operatori (pulsanti "segnala !" nel Report del giorno).
          Compaiono nella scheda <b>Disciplina</b> del Report operatori.
        </p>
        <ModalitaPicker value={discModalita} inUso={discData?.disc_modalita || "progressiva"} onChange={setDiscModalita}
          soglia={Number(discSoglia) || 5} periodo={Number(discPeriodo)} onPeriodo={setDiscPeriodo} scadenza={Number(discScadenza)} onScadenza={setDiscScadenza} />
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="disc-soglia">Soglia segnalazioni per un richiamo</Label>
            <Input id="disc-soglia" type="number" min={1} max={100} value={discSoglia}
              onChange={(e) => setDiscSoglia(e.target.value)} data-testid="disc-soglia-mese" />
            <p className="text-xs text-muted-foreground">Quando il contatore arriva a questo numero (secondo la modalità scelta sopra) parte una lettera di richiamo.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="disc-lettere">Lettere → Allerta critica</Label>
            <Input id="disc-lettere" type="number" min={1} max={50} value={discLettere}
              onChange={(e) => setDiscLettere(e.target.value)} data-testid="disc-lettere-allerta" />
            <p className="text-xs text-muted-foreground">Numero di lettere che porta allo stato di Allerta critica.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="disc-finestra">Finestra conteggio lettere (mesi)</Label>
            <Input id="disc-finestra" type="number" min={1} max={60} value={discFinestra}
              onChange={(e) => setDiscFinestra(e.target.value)} data-testid="disc-finestra-mesi" />
            <p className="text-xs text-muted-foreground">Periodo entro cui si contano le lettere (es. ultimi 12 mesi).</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="disc-sollecito">Sollecito presa visione (giorni)</Label>
            <Input id="disc-sollecito" type="number" min={1} max={60} value={discSollecito}
              onChange={(e) => setDiscSollecito(e.target.value)} data-testid="disc-sollecito-giorni" />
            <p className="text-xs text-muted-foreground">Dopo quanti giorni sollecitare automaticamente chi non ha confermato la presa visione.</p>
          </div>
        </div>
        <Button onClick={saveDisc} disabled={discSaving} className="font-semibold" data-testid="disc-save">
          <Save size={16} className="mr-1" /> {discSaving ? "Salvataggio..." : "Salva impostazioni disciplina"}
        </Button>
      </div>
      )}

      {cat === "sistema" && <DocumentiOrfaniPanel />}
    </>
  );

  return (
    <div className="space-y-5 max-w-5xl" data-testid="impostazioni-page">
      <div>
        <h1 className="text-2xl font-head font-bold">Impostazioni</h1>
        <p className="text-sm text-muted-foreground">Scegli una categoria per vedere e modificare tutte le sue impostazioni.</p>
      </div>
      <ImpTessere cat={cat} setCat={setCat} pannello={pannello} />
    </div>
  );
}
